import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Search } from "lucide-react";
import { useSearchCities } from "@/lib/api";
import { CityList } from "./CityList";
import { useT } from "@/lib/i18n";

interface CitySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCityId: string;
  onSelectCity: (cityId: string) => void;
}

export function CitySelectionModal({
  isOpen,
  onClose,
  selectedCityId,
  onSelectCity,
}: CitySelectionModalProps) {
  const { t } = useT();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: citiesData } = useSearchCities(debouncedQuery, selectedCityId);
  const cities = citiesData?.data || [];

  const handleSelectCity = (cityId: string) => {
    onSelectCity(cityId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 card flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="card modal-box border border-dark-border rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{t('citySelection.title')}</h2>
          <button
            onClick={onClose}
            className="text-dark-muted hover:text-primary-500 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-muted" />
          <input
            type="text"
            placeholder={t('citySelection.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg border border-divider focus:border-primary-500 focus:outline-none transition-colors"
          />
        </div>

        <CityList
          cities={cities}
          selectedCityId={selectedCityId}
          onSelectCity={handleSelectCity}
        />
      </motion.div>
    </div>
  );
}
